import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { WifiOff, RefreshCw, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { queueGet, flushKhataQueue, QUEUE_EVENT } from '../lib/khataQueue';

export default function OfflineBar() {
  const { user } = useAuth();
  const [offline, setOffline] = useState(!navigator.onLine);
  const [pending, setPending] = useState(() => queueGet().length);
  const [syncing, setSyncing] = useState(false);

  const sync = async (silent = false) => {
    if (!user || !queueGet().length || !navigator.onLine) return;
    // Khata screens sync and toast on their own.
    if (silent && window.__msKhataMounted) return;
    setSyncing(true);
    const { sent, remaining } = await flushKhataQueue();
    setSyncing(false);
    if (!silent || sent) toast[remaining.length ? 'info' : 'success'](
      remaining.length ? `${sent} synced, ${remaining.length} still waiting` : `${sent} offline Khata entr${sent === 1 ? 'y' : 'ies'} synced`);
  };

  useEffect(() => {
    const on = () => { setOffline(false); sync(true); };
    const off = () => setOffline(true);
    const q = (e) => setPending(e.detail);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    window.addEventListener(QUEUE_EVENT, q);
    sync(true);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.removeEventListener(QUEUE_EVENT, q);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!offline && !pending) return null;
  return (
    <div data-testid="offline-bar" className={`text-xs font-bold ${offline ? 'bg-ink text-white' : 'bg-[#FFF3E0] text-[#9A4500]'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center gap-2 flex-wrap">
        {offline ? <WifiOff className="w-4 h-4 shrink-0"/> : <CloudUpload className="w-4 h-4 shrink-0"/>}
        <span data-testid="offline-bar-text" className="flex-1 min-w-0">
          {offline ? 'No internet — you can still open your Khata. New entries are saved on this phone.' : 'Back online.'}
          {pending > 0 && <> <b data-testid="offline-bar-pending">{pending}</b> Khata entr{pending === 1 ? 'y' : 'ies'} waiting to sync.</>}
        </span>
        {offline ? (
          <Link to="/khata" data-testid="offline-bar-open-khata" className="underline">Open Khata</Link>
        ) : pending > 0 && (
          <button data-testid="offline-bar-sync" onClick={() => sync(false)} disabled={syncing} className="inline-flex items-center gap-1 underline">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`}/>Sync now
          </button>
        )}
      </div>
    </div>
  );
}
